// Pure helpers for the calibration overview: the calibration window (N complete
// Saturday→Friday weeks) and a data-quality status for that window.

import { addDays, startOfWeekSat } from "../../lib/format";

/**
 * The most recent `weeks` complete Saturday→Friday weeks ending before the
 * current (in-progress) week. e.g. on a Tuesday, the window ends last Friday.
 */
export function calibrationWindow(todayStr: string, weeks: number): { start: string; end: string } {
  const currentWeekStart = startOfWeekSat(todayStr); // this week's Saturday
  const end = addDays(currentWeekStart, -1); // previous Friday = last complete week end
  const start = addDays(currentWeekStart, -7 * weeks); // Saturday, `weeks` weeks earlier
  return { start, end };
}

export type CalLevel = "ok" | "warn" | "bad";
export interface CalStatus {
  level: CalLevel;
  label: string;
}

/**
 * Assess whether a calibration window is trustworthy: enough days of actuals,
 * and a heads-up when holidays fall in the window (they skew base + DOW).
 */
export function calibrationStatus(opts: {
  weeks: number;
  daysWithData: number;
  holidayCount: number;
  eventCount?: number;
}): CalStatus {
  const expected = opts.weeks * 7;
  if (opts.daysWithData === 0) return { level: "bad", label: "No actuals" };
  const missing = expected - opts.daysWithData;
  if (missing > 0) {
    return { level: "warn", label: `Missing ${missing} day${missing > 1 ? "s" : ""}` };
  }
  if ((opts.eventCount ?? 0) > 0) {
    return { level: "warn", label: `Hub event in window (${opts.eventCount})` };
  }
  if (opts.holidayCount > 0) {
    return { level: "warn", label: `Holiday in window (${opts.holidayCount})` };
  }
  return { level: "ok", label: "Ready" };
}
